import React, { useState, useRef, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useClinic } from '../../contexts/ClinicContext';

const { FiMenu, FiLogOut, FiUser, FiShield, FiChevronDown, FiLock, FiSettings } = FiIcons;

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { clinic_name, logo_url } = useClinic();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const profileMenuRef = useRef(null);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get clinic name initials for the logo fallback
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  // Get user initials for avatar fallback
  const getUserInitials = (name) => {
    if (!name) return "U";
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  // Get role badge color
  const getRoleBadgeColor = (role, isSysadmin) => {
    if (isSysadmin) {
      return 'bg-purple-100 text-purple-700';
    }
    switch (role) {
      case 'admin': return 'bg-maroon-100 text-maroon-700';
      case 'manager': return 'bg-blue-100 text-blue-700';
      case 'doctor': return 'bg-green-100 text-green-700';
      case 'receptionist': return 'bg-orange-100 text-orange-700';
      default: return 'bg-grey-100 text-grey-700';
    }
  };

  // Get role display name
  const getRoleDisplayName = (role, isSysadmin) => {
    if (isSysadmin) {
      return 'System Admin';
    }
    return role;
  };

  const openPasswordModal = () => {
    setShowProfileMenu(false);
    setShowPasswordModal(true);
  };

  return (
    <>
      <header className="bg-white shadow-sm border-b border-grey-200">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center">
            <button
              onClick={onMenuClick}
              className="p-2 rounded-md text-grey-400 hover:text-grey-600 hover:bg-grey-100"
            >
              <SafeIcon icon={FiMenu} className="w-6 h-6" />
            </button>

            <div className="flex items-center ml-4">
              {logo_url ? (
                <div className="w-8 h-8 overflow-hidden rounded-lg mr-2 flex items-center justify-center">
                  <img src={logo_url} alt={clinic_name} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-8 h-8 bg-maroon-600 rounded-lg mr-2 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{getInitials(clinic_name)}</span>
                </div>
              )}
              <h1 className="text-2xl font-semibold text-grey-800">
                {clinic_name}
              </h1>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Profile Menu */}
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center space-x-3 text-sm text-grey-600 hover:bg-grey-100 rounded-lg px-3 py-2 transition-colors"
              >
                {/* Show profile image if user is a doctor and has profile_image */}
                {user?.role === 'doctor' && user?.profile_image ? (
                  <div className="h-8 w-8 rounded-full overflow-hidden">
                    <img src={user.profile_image} alt={user.name} className="h-full w-full object-cover" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-8 w-8 bg-maroon-100 text-maroon-700 rounded-full">
                    {user?.isSysadmin ? (
                      <SafeIcon icon={FiShield} className="w-4 h-4" />
                    ) : (
                      <span className="font-medium text-sm">{getUserInitials(user?.name)}</span>
                    )}
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <div className="text-left">
                    <div className="font-medium text-grey-900">{user?.name}</div>
                    <div className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(user?.role, user?.isSysadmin)}`}>
                      {getRoleDisplayName(user?.role, user?.isSysadmin)}
                    </div>
                  </div>
                  <SafeIcon icon={FiChevronDown} className="w-4 h-4 text-grey-400" />
                </div>
              </button>

              {/* Profile Dropdown Menu */}
              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-grey-200 py-1 z-50">
                  <div className="px-4 py-3 border-b border-grey-200">
                    <div className="text-sm font-medium text-grey-900">{user?.name}</div>
                    <div className="text-sm text-grey-500">{user?.email}</div>
                  </div>
                  
                  {/* Only show change password for non-sysadmin users */}
                  {!user?.isSysadmin && (
                    <button
                      onClick={openPasswordModal}
                      className="flex items-center w-full px-4 py-2 text-sm text-grey-700 hover:bg-grey-100"
                    >
                      <SafeIcon icon={FiLock} className="w-4 h-4 mr-3" />
                      Change Password
                    </button>
                  )}

                  {/* Settings link for admin users */}
                  {user?.role === 'admin' && (
                    <a
                      href="#/settings"
                      className="flex items-center w-full px-4 py-2 text-sm text-grey-700 hover:bg-grey-100"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <SafeIcon icon={FiSettings} className="w-4 h-4 mr-3" />
                      Settings
                    </a>
                  )}

                  <div className="border-t border-grey-200">
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        logout();
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <SafeIcon icon={FiLogOut} className="w-4 h-4 mr-3" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <ChangePasswordModal
          onClose={() => setShowPasswordModal(false)}
          user={user}
        />
      )}
    </>
  );
};

// Change Password Modal Component
const ChangePasswordModal = ({ onClose, user }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate passwords
    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to change password');
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-grey-900">Change Password</h3>
          <button
            onClick={onClose}
            className="text-grey-400 hover:text-grey-500"
          >
            <SafeIcon icon={FiIcons.FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <div className="text-sm text-blue-800">
            <div className="font-medium">{user.name}</div>
            <div className="text-blue-600">{user.email}</div>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <SafeIcon icon={FiIcons.FiAlertTriangle} className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">Password changed successfully!</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">
              Current Password
            </label>
            <input
              type="password"
              name="currentPassword"
              required
              className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
              value={formData.currentPassword}
              onChange={handleInputChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">
              New Password
            </label>
            <input
              type="password"
              name="newPassword"
              required
              minLength="6"
              className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
              value={formData.newPassword}
              onChange={handleInputChange}
            />
            <p className="text-xs text-grey-500 mt-1">Must be at least 6 characters long</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              name="confirmPassword"
              required
              className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
              value={formData.confirmPassword}
              onChange={handleInputChange}
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || success}
              className="flex-1 px-4 py-2 bg-maroon-600 text-white rounded-lg hover:bg-maroon-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Header;